import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export const useSecurity = () => {
  const { tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const withAuthHeaders = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${tokenId}`,
      },
    }),
    [tokenId]
  );

  const handleSuccess = useCallback(
    (header: string, subheader?: string) => {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader,
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    },
    [addToast]
  );

  const handleError = useCallback(
    (error: AxiosError, header: string) => {
      // eslint-disable-next-line no-console
      console.error(error);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader: ((error as AxiosError)?.response?.data as any)?.error,
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
    },
    [addToast]
  );

  const pauseInstrument = useCallback(
    async (instrument_name: string) => {
      try {
        await axios.post(
          ADMIN_API_URL(AdminEndpointEnum.PAUSE_INSTRUMENT),
          { instrument_name },
          withAuthHeaders()
        );
        handleSuccess("Instrument paused", instrument_name);
      } catch (e) {
        handleError(e as AxiosError, "Pause instrument failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  const resumeInstrument = useCallback(
    async (instrument_name: string) => {
      try {
        await axios.post(
          ADMIN_API_URL(AdminEndpointEnum.RESUME_INSTRUMENT),
          { instrument_name },
          withAuthHeaders()
        );
        handleSuccess("Instrument resumed", instrument_name);
      } catch (e) {
        handleError(e as AxiosError, "Resume instrument failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  const resumeAllInstruments = useCallback(async () => {
    try {
      await axios.post(
        ADMIN_API_URL(AdminEndpointEnum.RESUME_ALL_INSTRUMENTS),
        {},
        withAuthHeaders()
      );
      handleSuccess("All instruments resumed");
    } catch (e) {
      handleError(e as AxiosError, "Resume all instruments failed");
    }
  }, [handleError, handleSuccess, withAuthHeaders]);

  const pauseTrading = useCallback(async () => {
    try {
      await axios.post(
        ADMIN_API_URL(AdminEndpointEnum.PAUSE_TRADING),
        {},
        withAuthHeaders()
      );
      handleSuccess("Trading paused");
    } catch (e) {
      handleError(e as AxiosError, "Pause trading failed");
    }
  }, [handleError, handleSuccess, withAuthHeaders]);

  const resumeTrading = useCallback(async () => {
    try {
      await axios.post(
        ADMIN_API_URL(AdminEndpointEnum.RESUME_TRADING),
        {},
        withAuthHeaders()
      );
      handleSuccess("Trading resumed");
    } catch (e) {
      handleError(e as AxiosError, "Resume trading failed");
    }
  }, [handleError, handleSuccess, withAuthHeaders]);

  const pauseWithdraw = useCallback(
    async (target: string) => {
      try {
        await axios.post(
          ADMIN_API_URL(AdminEndpointEnum.PAUSE_WITHDRAW),
          { target },
          withAuthHeaders()
        );
        handleSuccess("Withdrawals paused", target);
      } catch (e) {
        handleError(e as AxiosError, "Pause withdrawals failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  const resumeWithdraw = useCallback(
    async (target: string) => {
      try {
        await axios.post(
          ADMIN_API_URL(AdminEndpointEnum.RESUME_WITHDRAW),
          { target },
          withAuthHeaders()
        );
        handleSuccess("Withdrawals resumed", target);
      } catch (e) {
        handleError(e as AxiosError, "Resume withdrawals failed");
      }
    },
    [handleError, handleSuccess, withAuthHeaders]
  );

  return {
    pauseInstrument,
    resumeInstrument,
    resumeAllInstruments,
    pauseTrading,
    resumeTrading,
    pauseWithdraw,
    resumeWithdraw,
  };
};
